import type { ComponentType, ReactNode, SVGProps } from 'react'
import { addDays } from 'date-fns'
import { Ban, CalendarCheck, Check, CircleDashed, TriangleAlert, UserX, X } from 'lucide-react'
import type { CleaningJob, CleaningTeam } from '@/api/client'
import { STAY_FOCUS_RING } from '@/components/calendar/stay-bars'
import { toCalendarDate, type MonthBar } from '@/components/calendar/month-grid'
import { cn } from '@/lib/utils'

/**
 * How a cleaning is drawn, everywhere it is drawn: one place, so the cleaning
 * board's context calendar, the property calendar, its legend and the sheets
 * cannot disagree about what a colour means (CAP-894).
 *
 * The question a cleaning answers is "is the turnover done, and if not, is it
 * on track" - and only then "who". So every state is a word AND an icon AND a
 * colour (never colour alone), and the most important state, done, is the
 * loudest one rather than the faintest:
 *
 * - cleaned: solid green with a check - the one thing nobody needs to chase;
 * - needs a decision: solid amber with a warning - the one thing that needs
 *   you today;
 * - scheduled: a plain bar with a calendar tick - confirmed and on track;
 * - tentative: dashed with a dashed circle - nothing is committed, so it must
 *   never read as a promise;
 * - not required: struck and quiet - a decision somebody made;
 * - and a job nobody is doing says "No crew" in words, because a missing dot
 *   is not something a reader notices.
 *
 * Which state a job is in comes from the job: `status`, the `substate` admin-api
 * derives (CAP-531) and `confirmed_at`. Whether a scheduled cleaning is
 * overdue is NOT here: the job row carries no such field, and working it out
 * would mean this console comparing dates itself.
 */

export type CleaningState =
  'cleaned' | 'scheduled' | 'tentative' | 'needs_decision' | 'not_required' | 'cancelled'

type IconType = ComponentType<SVGProps<SVGSVGElement> & { strokeWidth?: number | string }>

interface CleaningStateMeta {
  /** The word, the same on the bar, the legend and both sheets. */
  label: string
  /** A shorter word for a bar too narrow for `label` but wide enough for a word. */
  shortLabel?: string
  Icon: IconType
  /** Colours of the bar, the legend chip and the sheet's chip. */
  classes: string
  /** What the state means, for the legend's tooltip. */
  meaning: string
  /** Heavier strokes for the two states that must read at a glance. */
  strong?: boolean
}

export const CLEANING_STATE_META: Record<CleaningState, CleaningStateMeta> = {
  cleaned: {
    label: 'Cleaned',
    Icon: Check,
    classes: 'bg-mint border-mint text-charcoal font-semibold',
    meaning: 'The crew finished this cleaning.',
    strong: true,
  },
  needs_decision: {
    label: 'Needs a decision',
    shortLabel: 'Decide',
    Icon: TriangleAlert,
    classes: 'bg-amber border-amber text-charcoal font-semibold',
    meaning: 'Blocked in Uplisting - nobody has said whether it needs a clean.',
    strong: true,
  },
  scheduled: {
    label: 'Scheduled',
    Icon: CalendarCheck,
    classes: 'bg-surface2 border-textMuted text-text',
    meaning: 'Confirmed for this day.',
  },
  tentative: {
    label: 'Tentative',
    Icon: CircleDashed,
    classes: 'bg-surface2 border-dashed border-textMuted text-textMuted',
    meaning: 'Not confirmed yet - nothing is promised to the crew.',
  },
  not_required: {
    label: 'No clean',
    Icon: Ban,
    classes: 'border-border text-textMuted line-through',
    meaning: 'Decided: no cleaning needed.',
  },
  // Never drawn on a grid and not in the legend (`isCleaningDrawn`), but a
  // sheet can still be opened on one - from a pasted link, or as a stay's only
  // turnover job - and it must not read as a live cleaning there.
  cancelled: {
    label: 'Cancelled',
    Icon: X,
    classes: 'border-border text-textMuted',
    meaning: 'This cleaning was cancelled.',
  },
}

/** The order the legend lists them in: done first, then what wants you. */
export const CLEANING_STATE_ORDER: readonly CleaningState[] = [
  'cleaned',
  'scheduled',
  'tentative',
  'needs_decision',
  'not_required',
]

/** Said whenever a cleaning that should have a crew has none. */
export const NO_CREW_LABEL = 'No crew'
const NO_CREW_CLASSES = 'text-amber'

/** The job the operator came here about - the same ring the stays use. */
export const CLEANING_FOCUS_RING = STAY_FOCUS_RING
/**
 * The same ring in the dark ink of a solid green bar: a mint ring on a mint bar
 * is invisible, which would stop the opened job being marked in the most common
 * state.
 */
export const CLEANING_FOCUS_RING_ON_SOLID_GREEN = 'ring-2 ring-inset ring-charcoal'

/** The band cleanings sit in: below every stay and block (tier 0). */
export const CLEANING_TIER = 1

/**
 * Which state a (drawn) cleaning is in.
 *
 * `substate` is admin-api's own answer for a scheduled job (CAP-531) and wins
 * when present; the raw fields cover a job that carries none. A cancelled job
 * is never drawn (`isCleaningDrawn`), so it has no state of its own.
 */
export function cleaningState(job: CleaningJob): CleaningState {
  if (job.status === 'cancelled') return 'cancelled'
  if (job.status === 'not_required') return 'not_required'
  if (job.status === 'completed') return 'cleaned'
  if (job.needs_decision || job.substate === 'needs_decision') return 'needs_decision'
  const tentative = job.substate ? job.substate === 'tentative' : !job.confirmed_at
  return tentative ? 'tentative' : 'scheduled'
}

/**
 * Whether the cleaning is still to be done and nobody is down to do it.
 * Not said for a finished or decided-against one - there it is history.
 */
export function isCrewMissing(job: CleaningJob): boolean {
  const state = cleaningState(job)
  return (state === 'scheduled' || state === 'tentative') && !job.team_id
}

/**
 * The crew's short name, or - when the crews have not loaded, or this one has
 * since been removed - the id the job carries. Never "No crew" for a job that
 * has one: that would read as an unassigned cleaning.
 */
function crewShortName(job: CleaningJob, team: CleaningTeam | undefined): string | null {
  return team?.short_name ?? job.team_id ?? null
}

/**
 * Whether a cleaning gets a bar at all.
 *
 * A cancelled cleaning is drawn nowhere, because the thing it followed is
 * drawn nowhere either: a cancelled reservation is filtered out of the stays,
 * so its cleaning would sit alone on an empty day saying "Cancelled" about
 * nothing visible. A not-required one stays - that is a decision somebody made
 * about a stay or block that IS on the grid, and it is the most useful
 * precedent for the next one.
 */
export function isCleaningDrawn(job: CleaningJob): boolean {
  return job.status !== 'cancelled'
}

/**
 * Whether the crew's name is still on its way: the crew list has not arrived and
 * this job names a crew. Until it does, a job's own `team_id` is not a name, and
 * showing it would flash an id where the name goes.
 */
function crewPending(job: CleaningJob, team: CleaningTeam | undefined, crewLoading: boolean) {
  return crewLoading && !team && !!job.team_id
}

/** What a cleaning bar says in the space of a day cell, whole. */
export function cleaningLabel(
  job: CleaningJob,
  team: CleaningTeam | undefined,
  crewLoading = false
): string {
  const word = CLEANING_STATE_META[cleaningState(job)].label
  if (['not_required', 'needs_decision', 'cancelled'].includes(cleaningState(job))) return word
  if (crewPending(job, team, crewLoading)) return word
  const crew = crewShortName(job, team)
  if (crew) return `${word} · ${crew}`
  return isCrewMissing(job) ? `${word} · ${NO_CREW_LABEL}` : word
}

/** The whole of it, on hover, where there is room to say it. */
export function cleaningTitle(
  job: CleaningJob,
  team: CleaningTeam | undefined,
  crewLoading = false
): string {
  const state = cleaningState(job)
  const crew = crewPending(job, team, crewLoading)
    ? 'its crew (name loading)'
    : team
      ? team.name
      : (job.team_id ?? 'nobody assigned')
  switch (state) {
    case 'not_required':
      return 'Decided: no cleaning needed'
    case 'cancelled':
      return 'Cancelled'
    case 'cleaned':
      return `Cleaned by ${crew}`
    case 'needs_decision':
      return 'Blocked in Uplisting - nobody has said whether it needs a clean'
    case 'tentative':
      return `Tentative, not confirmed yet - ${isCrewMissing(job) ? NO_CREW_LABEL : crew}`
    case 'scheduled':
      // "Confirmed" is the booking's, not the crew's: say when they have not
      // acked, as the sheet's "No ack" does.
      return `Scheduled and confirmed - ${isCrewMissing(job) ? NO_CREW_LABEL : crew}${
        job.substate === 'needs_ack' ? '. No ack from the crew yet' : ''
      }`
  }
}

/** The state's icon and word, in the state's colours: the legend's chip and the sheets'. */
export function CleaningStatusChip({
  state,
  noCrew = false,
  className,
  testId,
}: {
  state: CleaningState
  /** Adds the explicit "No crew" after the state. */
  noCrew?: boolean
  className?: string
  testId?: string
}) {
  const { label, Icon, classes, strong } = CLEANING_STATE_META[state]
  return (
    <span
      className="inline-flex min-w-0 flex-wrap items-center justify-end gap-1.5"
      data-testid={testId}
    >
      <span
        data-cleaning-state={state}
        className={cn(
          'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] leading-4',
          classes,
          className
        )}
      >
        <Icon className="h-3 w-3 shrink-0" strokeWidth={strong ? 3 : 2} aria-hidden="true" />
        {label}
      </span>
      {noCrew && <NoCrew />}
    </span>
  )
}

function NoCrew({ inBar = false }: { inBar?: boolean }) {
  return (
    <span
      data-cleaning-no-crew=""
      className={cn('inline-flex shrink-0 items-center gap-1 font-semibold', NO_CREW_CLASSES)}
    >
      <UserX className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className={cn(inBar && BAR_WORD)}>{NO_CREW_LABEL}</span>
    </span>
  )
}

/** Every state a cleaning can be drawn in, as the bars draw them. */
export function CleaningLegend(): ReactNode {
  return (
    <>
      {CLEANING_STATE_ORDER.map(state => (
        <span
          key={state}
          title={CLEANING_STATE_META[state].meaning}
          data-testid={`cleaning-legend-${state}`}
        >
          <CleaningStatusChip state={state} />
        </span>
      ))}
      <span
        title="Still to be done, and nobody is down to do it."
        data-testid="cleaning-legend-no-crew"
      >
        <span className="inline-flex items-center rounded border border-border bg-surface2 px-1.5 py-0.5 text-[11px] leading-4">
          <NoCrew />
        </span>
      </span>
    </>
  )
}

interface CleaningBarOptions {
  /** The property-local day the cleaning sits on. */
  day: string
  team: CleaningTeam | undefined
  /**
   * The crew list has not arrived. A bar that names a crew then shows a quiet
   * placeholder where the name and colour go - same size, so nothing moves when
   * the crew lands - instead of the crew's id in a default colour.
   */
  crewLoading?: boolean
  /** Ringed, because it is the one the operator is looking at. */
  focused?: boolean
  testId: string
  onClick?: () => void
}

/**
 * What a bar shows depends on how wide ITS cell is, not on the screen: the
 * same bar sits in a 190px cell on a wide monitor and a 35px one on a phone,
 * and the grid's card can be narrower than the screen. So the label is a
 * container (CSS container query on the bar's own content width) and sheds, in
 * order: the crew's name, then the word, then everything but icon and colour.
 * What is shed stays in the DOM for a screen reader (`sr-only`).
 */
const BAR_WORD = 'sr-only @min-[64px]:not-sr-only'
const BAR_CREW = 'sr-only @min-[100px]:not-sr-only @min-[100px]:min-w-0 @min-[100px]:truncate'
/** "Needs a decision" needs ~124px; below that the shorter word does. */
const BAR_FULL_WORD = 'sr-only @min-[124px]:not-sr-only'
const BAR_SHORT_WORD = 'hidden @min-[64px]:inline @min-[124px]:hidden'
/** Status word next to "No crew": both only fit in a wide cell, and "No crew" wins. */
const BAR_WORD_WITH_NO_CREW = 'sr-only @min-[144px]:not-sr-only'

export function buildCleaningBar(job: CleaningJob, options: CleaningBarOptions): MonthBar {
  const { day, team, crewLoading = false, focused = false, testId, onClick } = options
  const start = toCalendarDate(day)
  const title = cleaningTitle(job, team, crewLoading)
  const label = cleaningLabel(job, team, crewLoading)
  const state = cleaningState(job)
  const { label: word, shortLabel, Icon, classes, strong } = CLEANING_STATE_META[state]
  const crew =
    state === 'not_required' || state === 'needs_decision' || state === 'cancelled'
      ? null
      : crewShortName(job, team)
  const noCrew = isCrewMissing(job)
  const crewComing = crew !== null && crewPending(job, team, crewLoading)
  return {
    key: `cleaning-${job.id}`,
    start,
    endExclusive: addDays(start, 1),
    tier: CLEANING_TIER,
    testId,
    data: {
      'data-job-id': job.id,
      'data-cleaning-state': state,
      ...(crewComing ? { 'data-crew-loading': 'true' } : {}),
    },
    title,
    ariaLabel: onClick ? `${label} - ${title}` : undefined,
    onClick,
    className: cn(
      classes,
      focused && (state === 'cleaned' ? CLEANING_FOCUS_RING_ON_SOLID_GREEN : CLEANING_FOCUS_RING)
    ),
    label: (
      <span className="@container block min-w-0">
        <span className="flex min-w-0 items-center gap-1">
          <Icon className="h-3 w-3 shrink-0" strokeWidth={strong ? 3 : 2} aria-hidden="true" />
          {shortLabel ? (
            <>
              <span className={BAR_FULL_WORD}>{word}</span>
              <span aria-hidden="true" className={BAR_SHORT_WORD}>
                {shortLabel}
              </span>
            </>
          ) : (
            <span className={noCrew ? BAR_WORD_WITH_NO_CREW : BAR_WORD}>{word}</span>
          )}
          {crew && !strong && (
            <span
              aria-hidden="true"
              data-testid="cleaning-crew-dot"
              className={cn(
                'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                crewComing && 'animate-pulse bg-textFaint/50 motion-reduce:animate-none'
              )}
              style={
                crewComing
                  ? undefined
                  : { backgroundColor: team?.color ?? 'var(--color-textFaint)' }
              }
            />
          )}
          {/* The dot says who in a cell too small for the name; a solid bar has
              no dot (the crew's own colour can be the bar's - Elisa is green),
              so there the name carries it, with its own separator. */}
          {crew &&
            (crewComing ? (
              <span className={BAR_CREW} data-testid="cleaning-crew-loading">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-10 animate-pulse rounded-sm bg-current align-middle opacity-20 motion-reduce:animate-none"
                />
                <span className="sr-only">crew loading</span>
              </span>
            ) : (
              <span className={BAR_CREW}>{strong ? `· ${crew}` : crew}</span>
            ))}
          {noCrew && <NoCrew inBar />}
        </span>
      </span>
    ),
  }
}
